"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const _ = require("lodash");
function stringify(style) {
    const kebabCase = (str) => str.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase();
    return `${_(style).reduce((a, v, k) => [...a, `${kebabCase(k)}: ${v}`], []).join(';')}`;
}
const top = comp => comp.top;
const left = comp => comp.left;
const bottom = comp => comp.top + comp.height;
const right = comp => comp.left + comp.width;
const fitToContentHeight = (container) => Boolean(container.fitToContentHeight);
const compVerticalArea = (comp) => ({ top: top(comp), bottom: bottom(comp) });
const compHorizontalArea = (comp) => ({ left: left(comp), right: right(comp) });
const unitize = (n) => _.isString(n) ? n : `${n}px`;
const formatMargin = (margin) => _.isNumber(margin) ? unitize(margin) : (asDock => `calc(${unitize(asDock.px)} + ${asDock.pct}%)`)(margin);
const isTightRow = (prevRowArea, nextRowArea) => prevRowArea.bottom === nextRowArea.top;
const distanceBetweenRows = (prevRowArea, nextRowArea) => nextRowArea.top - prevRowArea.bottom;
const getWedgeComp = (top, height, rowIndex, columnIndex, rootId) => {
    const additionalHeight = top < 0 ? top * -1 : 0;
    // console.log("getWedgeComp= ", top, height, additionalHeight);
    return {
        id: `${rootId}-wedge-${rowIndex}-${columnIndex}`,
        left: 0,
        top,
        width: 0,
        height: height + additionalHeight,
        wedge: true
    };
};
const addSpacersRows = (rows, gridVerticalArea, rootId, softMargin, minLockDistance) => {
    const rowsWithSpacers = [];
    const addRow = (area, components, subRows) => rowsWithSpacers.push({ area, components, subRows });
    const addSpacerRow = (prevRowArea, nextRowArea, r, c) => addRow({
        bottom: nextRowArea.top,
        top: prevRowArea.bottom
    }, [getWedgeComp(gridVerticalArea.top, nextRowArea.top, r, c, rootId)], []);
    addRow(rows[0].area, rows[0].components, rows[0].subRows);
    for (let i = 1; i < rows.length; i++) {
        const prevRowArea = rows[i - 1].area;
        const rowArea = rows[i].area;
        const rowComponents = rows[i].components;
        const subRows = rows[i].subRows;
        if (!isTightRow(prevRowArea, rowArea)) {
            const distance = distanceBetweenRows(prevRowArea, rowArea);
            if (distance <= minLockDistance) {
                const newRowArea = _.defaults({ top: rowArea.top - distance }, rowArea);
                addRow(newRowArea, rowComponents, subRows);
            }
            else {
                const newRowArea = _.defaults({ top: rowArea.top - softMargin }, rowArea);
                addSpacerRow(prevRowArea, newRowArea, i, i); // TODO ask kobiz about this -
                addRow(newRowArea, rowComponents, subRows);
            }
        }
        else {
            addRow(rowArea, rowComponents, subRows);
        }
    }
    return rowsWithSpacers;
};
const getRowsAreas = (mainRows) => _.reduce(mainRows, (result, { area, subRows }) => {
    if (_.isEmpty(subRows)) {
        result.push(Object.assign({}, area, { isSubRow: false }));
    }
    else {
        const subRowsRanges = [area.top, ...subRows, area.bottom];
        for (let i = 1; i < subRowsRanges.length; i++) {
            result.push({
                top: subRowsRanges[i - 1],
                bottom: subRowsRanges[i],
                isSubRow: true
            });
        }
    }
    return result;
}, []);
const gridArea = (ie) => (r1, c1, r2, c2) => (ie ? {
    '-ms-grid-column': c1.toString(),
    '-ms-grid-column-span': (c2 - c1).toString(),
    '-ms-grid-row': r1.toString(),
    '-ms-grid-row-span': (r2 - r1).toString()
} :
    { gridArea: [r1, c1, r2, c2].join(' / ') });
const findStartRowIndex = (rows, comp) => _.findLastIndex(rows, (area) => (area.top <= top(comp)));
const findEndRowIndex = (rows, comp) => _.findIndex(rows, (area) => (area.bottom >= bottom(comp)));
const isLast = (index, arr) => index === arr.length - 1;
const getLockMarginBottom = (comp, lastRowArea, softMargin, minLockMargin) => {
    const distanceFromRowBottom = lastRowArea.bottom - bottom(comp);
    if (distanceFromRowBottom > minLockMargin) {
        return softMargin;
    }
    return _.min([distanceFromRowBottom, minLockMargin]);
};
const getLockedMarginBottom = (comp, lastRowArea) => {
    return lastRowArea.bottom - bottom(comp);
};
const getMarginBottom = (comp, rows, endRowIndex, container, softMargin, minLockMargin) => {
    const endRow = rows[endRowIndex];
    const isSubRow = endRow.isSubRow; // todo - q - is it ever true ?
    if (isLast(endRowIndex, rows)) {
        return fitToContentHeight(container) ? 0 : getLockMarginBottom(comp, endRow, softMargin, minLockMargin);
    }
    return isSubRow ? getLockedMarginBottom(comp, endRow) : 0;
};
const getMarginTop = (comp, rows, startRowIndex) => {
    return top(comp) - rows[startRowIndex].top;
};
const dockedToMargin = (margin) => {
    if (!margin) {
        return null;
    }
    const vals = _.compact([
        margin.px && `${margin.px}px`,
        margin.pct && `${margin.pct}%`,
        margin.vw && `${margin.vw}vw`
    ]);
    switch (_.size(vals)) {
        case 0: return '0';
        case 1: return vals[0];
        default:
            return `calc(${_.join(vals, ' + ')})`;
    }
};
const getMarginLeft = (comp, container) => dockedToMargin(comp.docked && comp.docked.left) ||
    (container.contentArea ? `calc((100% - ${container.contentArea.width}px) * ${container.contentArea.alignment})` : '0');
const getMarginRight = (comp) => dockedToMargin(comp.docked && comp.docked.right) || '0px';
const getJustify = (dock) => {
    const hdock = _.pick(dock, ['left', 'right']) || {};
    if (hdock.left && hdock.right) {
        return 'stretch';
    }
    if (hdock.right) {
        return 'end';
    }
    return 'start';
};
const dataFor = (rows, comp, container, softMargin, minLockMargin) => {
    const startRowIndex = findStartRowIndex(rows, comp);
    const endRowIndex = findEndRowIndex(rows, comp);
    return {
        justifySelf: getJustify(comp.docked),
        marginTop: getMarginTop(comp, rows, startRowIndex),
        marginBottom: getMarginBottom(comp, rows, endRowIndex, container, softMargin, minLockMargin),
        marginLeft: getMarginLeft(comp, container),
        marginRight: getMarginRight(comp),
        left: left(comp),
        rowStart: startRowIndex + 1,
        columnStart: 1,
        rowEnd: endRowIndex + 2,
        columnEnd: 2
    };
};
const sumVerticalArea = (area1, area2) => ({
    top: _.min([area1.top, area2.top]),
    bottom: _.max([area1.bottom, area2.bottom]),
});
const addCompVerticalArea = (verticalArea, comp) => sumVerticalArea(verticalArea, compVerticalArea(comp));
const sumHorizontalArea = (area1, area2) => ({
    left: _.min([area1.left, area2.left]),
    right: _.max([area1.right, area2.right]),
});
const addCompHorizontalArea = (horizontalArea, comp) => sumHorizontalArea(horizontalArea, compHorizontalArea(comp));
// containerArea top = currentRow.area.top = _.min([0, getMinTop(compsSortedByTop)]); // todo - implement in better way -> first line should start on 0
// containerArea bottom = containerHeight; // todo - implement in better way -> last line should end on container bottom
const getRowsByIntersection = (compsSortedByTop, gridVerticalArea) => {
    const createRowGroup = component => ({
        components: [component],
        area: compVerticalArea(component)
    });
    const addCompToRowGroup = (rowGroup, component) => ({
        components: rowGroup.components.concat([component]),
        area: addCompVerticalArea(rowGroup.area, component)
    });
    const isIntersectWithRow = (area, comp) => top(comp) < area.bottom;
    const groups = [];
    if (compsSortedByTop.length === 0) {
        return groups;
    }
    let currentRow = createRowGroup(compsSortedByTop[0]);
    currentRow.area.top = gridVerticalArea.top;
    for (let i = 1; i < compsSortedByTop.length; i++) {
        if (isIntersectWithRow(currentRow.area, compsSortedByTop[i])) {
            currentRow = addCompToRowGroup(currentRow, compsSortedByTop[i]);
        }
        else {
            groups.push(currentRow);
            currentRow = createRowGroup(compsSortedByTop[i]);
        }
    }
    currentRow.area.bottom = gridVerticalArea.bottom;
    groups.push(currentRow);
    return groups;
};
const getColumnsByIntersection = (compsSortedByLeft) => {
    const createColumnGroup = (component) => ({
        components: [component],
        area: compHorizontalArea(component)
    });
    const addCompToColumnGroup = (columnGroup, component) => ({
        components: columnGroup.components.concat([component]),
        area: addCompHorizontalArea(columnGroup.area, component)
    });
    const isIntersectWithColumn = (horizontalArea, comp) => left(comp) < horizontalArea.right;
    const groups = [];
    if (compsSortedByLeft.length === 0) {
        return groups;
    }
    let currentColumn = createColumnGroup(compsSortedByLeft[0]);
    for (let i = 1; i < compsSortedByLeft.length; i++) {
        if (isIntersectWithColumn(currentColumn.area, compsSortedByLeft[i])) {
            currentColumn = addCompToColumnGroup(currentColumn, compsSortedByLeft[i]);
        }
        else {
            groups.push(currentColumn);
            currentColumn = createColumnGroup(compsSortedByLeft[i]);
        }
    }
    groups.push(currentColumn);
    return groups;
};
const getContainerHeight = (container) => _.max([container.height || 0, container.minHeight || 0]);
const getGridVerticalArea = (container, compsSortedByTop, compsSortedByBottom) => {
    const areaTop = _.min([0, top(_.first(compsSortedByTop))]);
    const areaBottom = _.max([container.height || 0, container.minHeight || 0, bottom(_.last(compsSortedByBottom))]);
    return {
        top: areaTop,
        bottom: areaBottom
    };
};
const getWrapperLayout = (area) => ({
    left: area.left,
    top: area.top,
    width: area.right - area.left,
    height: area.bottom - area.top,
    fitToContentHeight: true
});
const createWrapperBlock = (components, verticalArea, horizontalArea, rowIndex, columnIndex, rootId) => {
    const wrapperArea = getWrapperLayout(Object.assign({}, verticalArea, horizontalArea));
    return Object.assign({ id: `${rootId}-dynamic-block-${rowIndex}-${columnIndex}` }, wrapperArea, { components: _.map(components, comp => (Object.assign({}, comp, { left: comp.left - wrapperArea.left, top: comp.top - wrapperArea.top }))), dynamicBlock: true });
};
const getColumnsComponents = (columns, rowArea, rowIndex, rootId) => {
    return _.map(columns, ({ components, area }, columnIndex) => {
        if (components.length === 1) {
            return components[0];
        }
        return createWrapperBlock(components, rowArea, area, columnIndex, rowIndex, rootId);
    });
};
const MAX_GRID_LEVEL = 3;
const getMainRows = (rowsByIntersection, gridVerticalArea, gridDepth, rootId, softLock, minLockMargin) => {
    const mainRows = _.map(rowsByIntersection, (rowGroup, rowIndex) => {
        const subRows = getSubRows(rowGroup);
        const isComplexRow = subRows.length > 0;
        const shouldSplitToColumns = isComplexRow && gridDepth < MAX_GRID_LEVEL;
        const components = rowGroup.components;
        if (shouldSplitToColumns) {
            const columns = getColumnsByIntersection(_.sortBy(components, left));
            const canSplitToColumns = columns.length > 1;
            if (canSplitToColumns) {
                return {
                    area: rowGroup.area,
                    components: getColumnsComponents(columns, rowGroup.area, rowIndex, rootId),
                    subRows: [] //  todo - check if there is a scenario that we need to recalc subRows
                };
            }
        }
        return {
            area: rowGroup.area,
            components,
            subRows
        };
    });
    return addSpacersRows(mainRows, gridVerticalArea, rootId, softLock, minLockMargin); // TODO - q - should it be part of get mainRows?
};
const getCompsBottom = (comps) => _(comps).map(bottom).uniq().value();
const getSubRowsPosition = (mainRowArea, compSortedByTop) => {
    const compsBottom = getCompsBottom(compSortedByTop);
    const breaks = _.sortBy([mainRowArea.top].concat(compsBottom));
    let currentBreakIndex = 0;
    const findNearestBreak = (compTop) => {
        while (breaks[currentBreakIndex + 1] < compTop) {
            currentBreakIndex++;
        }
        return breaks[currentBreakIndex];
    };
    return _(compSortedByTop)
        .map(top) // todo changed here to two map - doesn't realy matter though - just point free
        .map(findNearestBreak) // todo side effect inside of a map is somewhat unexpected
        .uniq()
        .reject(val => val === mainRowArea.top)
        .value();
};
const getSubRows = ({ area, components }) => {
    const sortedByTop = _.sortBy(components, top);
    return getSubRowsPosition(area, sortedByTop);
};
const getTemplateRowsValue = (rows) => _(rows)
    .map((row, index) => {
    if (isLast(index, rows)) {
        return '1fr';
    }
    return 'min-content';
    // return isAuto ? 'auto' : 'max-content'
})
    .join(' ');
const templateRows = (rows, ie) => ({ [ie ? '-ms-grid-rows' : 'gridTemplateRows']: getTemplateRowsValue(rows) }); // TODo rows here structure doesnt realy matter..
// const getMostBottom = (components) => _(components).map(bottom).sortBy().last();
const isFarFromCompBottom = (containerHeight, mostBottomChildY, minLockMargin) => ((containerHeight - mostBottomChildY) > minLockMargin);
const childrenContainerMinHeight = (comp, sortedByBottom, gridOverflow, minLockMargin) => {
    if (fitToContentHeight(comp)) {
        return _.isUndefined(comp.minHeight) ? 'auto' : unitize(comp.minHeight + gridOverflow.top);
    }
    const mostBottom = bottom(_.last(sortedByBottom));
    const isSoft = isFarFromCompBottom(comp.height, mostBottom, minLockMargin);
    if (isSoft) {
        return unitize(_.max([comp.height, comp.minHeight]) + gridOverflow.top);
    }
    return _.isUndefined(comp.minHeight) ? 'auto' : unitize(comp.minHeight + gridOverflow.top);
};
const getGridOverflow = (container, gridVerticalArea) => {
    const overflowFromTop = 0 - gridVerticalArea.top;
    const overflowFromBottom = fitToContentHeight(container) ? 0 : gridVerticalArea.bottom - getContainerHeight(container);
    return {
        top: overflowFromTop,
        bottom: overflowFromBottom
    };
};
const isGridOverflow = (gridOverflow) => gridOverflow.top > 0 || gridOverflow.bottom > 0;
const overflowDirectionToMarginKey = {
    top: 'marginTop',
    bottom: 'marginBottom',
};
const getOverflowGridMargins = gridOverflow => {
    return _(gridOverflow)
        .pickBy(distance => (distance > 0))
        .reduce((result, distance, direction) => {
        result[overflowDirectionToMarginKey[direction]] = unitize(distance * -1);
        return result;
    }, {});
};
const getRootContainerStyle = container => ({
    // width: 'auto',
    height: 'auto',
    width: container.width ? unitize(container.width) : ''
});
const getChildrenContainerWrapperStyle = gridOverflow => isGridOverflow(gridOverflow) ? { display: 'flex' } : {}; // todo - maybe we need to add if ie here for ie 10 - doest support display: flex;
const getChildrenContainerStyles = (ie, container, allRowsAreas, sortedByBottom, gridOverflow, minLockMargin) => (Object.assign({ position: 'relative', 
    // pointerEvents: 'none',
    display: ie ? '-ms-grid' : 'grid', 
    // width: container.width ? unitize(container.width) : '',
    height: 'auto', width: '100%', minHeight: childrenContainerMinHeight(container, sortedByBottom, gridOverflow, minLockMargin) }, getOverflowGridMargins(gridOverflow), templateRows(allRowsAreas, ie), { [ie ? '-ms-grid-columns' : 'gridTemplateColumns']: '100%' }));
const getRootStructure = (meshHierarchy, containerId) => {
    const getCompStructure = (compId, childrenIds = []) => ({
        id: compId,
        children: _.map(childrenIds, (childId) => getCompStructure(childId, meshHierarchy[childId]))
    });
    return getCompStructure(containerId, meshHierarchy[containerId]);
};
const getWedgeStyles = (rows, wedge, ie) => {
    const getGridArea = gridArea(ie);
    const startRowIndex = findStartRowIndex(rows, wedge);
    const endRowIndex = findEndRowIndex(rows, wedge);
    return Object.assign({ visibility: 'hidden', height: unitize(wedge.height), width: '0' }, getGridArea(startRowIndex + 1, 1, endRowIndex + 2, 2));
};
const getCompStyles = (allRows, comp, ie, container, softMargin, minLockMargin) => {
    const getGridArea = gridArea(ie);
    const { marginTop, marginRight, marginBottom, marginLeft, justifySelf, left, rowStart, columnStart, rowEnd, columnEnd } = dataFor(allRows, comp, container, softMargin, minLockMargin);
    return Object.assign({ position: 'relative', margin: `${formatMargin(marginTop)} ${marginRight} ${formatMargin(marginBottom)} ${marginLeft}`, left: _.has(comp, 'docked.left') ? 0 : unitize(left) }, getGridArea(rowStart, columnStart, rowEnd, columnEnd), { [ie ? '-ms-grid-column-align' : 'justifySelf']: justifySelf, [ie ? '-ms-grid-row-align' : 'alignSelf']: 'start' });
};
function getLeafStyle({ height, width, minHeight, fitToContentHeight = false }) {
    return {
        height: fitToContentHeight ? 'auto' : unitize(height),
        width: unitize(width),
        minHeight: !_.isUndefined(minHeight) ? unitize(minHeight) : ''
    };
}
// function createMeshData (container: Component, containerId: string, options: PartialMeshDataOptions, gridDepth = 0): {hierarchy: MeshHierarchyFlat} {
function createMeshData(container, containerId, { cssGridVariant, softMargin, minLockMargin }, gridDepth = 0) {
    const comps = container.components;
    if (_.isEmpty(comps)) {
        return {
            hierarchy: { [containerId]: [] },
            styles: { [containerId]: getLeafStyle(container) }
        };
    }
    const ie = cssGridVariant === 'ms';
    const sortedByTop = _.sortBy(comps, top);
    const sortedByBottom = _.sortBy(comps, bottom);
    const gridVerticalArea = getGridVerticalArea(container, sortedByTop, sortedByBottom);
    const rowsByIntersection = getRowsByIntersection(sortedByTop, gridVerticalArea);
    const mainRows = getMainRows(rowsByIntersection, gridVerticalArea, gridDepth, containerId, softMargin, minLockMargin);
    const allComponents = _(mainRows).map('components').flatten().value();
    const allRowsAreas = getRowsAreas(mainRows);
    // consider move this code to private func
    const gridOverflow = getGridOverflow(container, gridVerticalArea);
    const containerStyle = getRootContainerStyle(container);
    const childrenWrapperStyle = getChildrenContainerWrapperStyle(gridOverflow);
    const childrenContainerStyles = getChildrenContainerStyles(ie, container, allRowsAreas, sortedByBottom, gridOverflow, minLockMargin);
    const gridWrapperId = `${containerId}-gridWrapper`;
    const gridContentId = `${containerId}-gridContainer`;
    const meshHierarchy = {
        [containerId]: [
            gridWrapperId
        ],
        [gridWrapperId]: [
            gridContentId
        ],
        [gridContentId]: []
    };
    const meshStyles = {
        [containerId]: containerStyle,
        [gridWrapperId]: childrenWrapperStyle,
        [gridContentId]: childrenContainerStyles
    };
    const addChildToMesh = (child) => {
        meshHierarchy[gridContentId].push(child.id);
        if (child.wedge) {
            _.assign(meshStyles, { [child.id]: getWedgeStyles(allRowsAreas, child, ie) });
            return;
        }
        _.assign(meshStyles, { [child.id]: getCompStyles(allRowsAreas, child, ie, container, softMargin, minLockMargin) });
        if (child.dynamicBlock) {
            const blockMeshData = createMeshData(child, child.id, { cssGridVariant, softMargin, minLockMargin }, gridDepth + 1);
            _.forEach(blockMeshData.styles, (styles, compId) => {
                meshStyles[compId] = _.assign({}, meshStyles[compId], styles);
            });
            _.assign(meshHierarchy, blockMeshData.hierarchy);
        }
    };
    _.forEach(allComponents, addChildToMesh);
    return {
        hierarchy: meshHierarchy,
        styles: meshStyles
    };
}
exports.createMeshData = createMeshData;
function id(x) { return x; }
function structure2mesh(container, { cssGridVariant = 'standard', softMargin = 10, minLockMargin = 70 } = {}) {
    const containerId = _.isUndefined(container.id) ? 'root' : container.id;
    const { hierarchy, styles } = createMeshData(container, containerId, { cssGridVariant, softMargin, minLockMargin });
    return {
        structure: getRootStructure(hierarchy, containerId),
        styles
    };
}
exports.structure2mesh = structure2mesh;
//# sourceMappingURL=structure2mesh.js.map